import { logError } from '../logging';

/**
 * Flattens a metaobject's fields into a simple key-value object.
 * @param {Object} metaobject - The metaobject containing fields and id.
 * @returns {Object} Flattened object with field keys mapped to values.
 */
export const flattenMetaobjectResponse = ({ metaobject }) => {
  if (!metaobject || !Array.isArray(metaobject.fields)) return {};
  const flat = { id: metaobject.id, handle: metaobject.handle };
  // Loop through each field and assign key-value pairs.
  for (const field of metaobject.fields) {
    let value = field.value;

    // At least try checking if the value is a json object.
    if (value.includes('{')) {
      try {
        value = JSON.parse(value);
      } catch (error) {
        logError({
          label: 'flattenMetaobjectResponse',
          error,
          message: 'Error parsing metaobject JSON',
          extraData: { fieldKey: field.key, value }
        });
        // Ignore
      }
    }
    // Handle field references (like Page references)
    if (field.reference) {
      flat[field.key] = field.reference;
    } else {
      flat[field.key] = value;
    }

    if (field.productVariants?.nodes) {
      // Only assign if nodes is a non-empty array and at least one node has a property (not just empty objects)
      const nodes = field.productVariants.nodes;
      const hasNonEmptyNode = nodes.some((node) => Object.keys(node).length > 0);
      if (hasNonEmptyNode) {
        flat.productVariants = nodes;
      }
    }
  }

  return flat;
};

/**
 * Flattens an array of metaobject references into an array of flat objects.
 * @param {Object} options - Options object.
 * @param {Array<Object>} options.metaobjects - Array of metaobject references.
 * @returns {Array<Object>} Array of flattened objects.
 */
export const flattenMetobjectArrayResponse = ({ metaobjects }) => {
  if (!Array.isArray(metaobjects)) return [];
  return metaobjects.map((metaobject) => flattenMetaobjectResponse({ metaobject }));
};
